<template>
  <div class="flex items-center gap-4">
    <FloatingMenu placement="bottom-start" :offset="[0, 8]" :z-index="110">
      <template #activator="{ show }">
        <button
          v-tooltip="
            review.entity.value.studies.length === 0
              ? 'You need to import at least one study'
              : 'Filters'
          "
          class="flex items-center gap-1 rounded-md hover:bg-gray-100 transition-colors"
          :class="[
            review.entity.value.studies.length === 0
              ? 'cursor-not-allowed opacity-50'
              : '',
            hasActiveFilters
              ? 'bg-primary-50 text-primary-600'
              : 'text-neutral-text-title',
          ]"
          :disabled="review.entity.value.studies.length === 0"
          @click="show"
        >
          <AdjustmentsVerticalIcon class="w-4 h-4" />
          <span v-if="hasActiveFilters" class="text-xs font-medium">
            {{ activeFilterCount }}
          </span>
        </button>
      </template>

      <template #content>
        <div class="w-80 p-4 space-y-4">
          <div class="flex items-center justify-between">
            <h3 class="font-medium text-gray-900">Filters</h3>
            <button
              v-if="hasActiveFilters"
              class="text-xs text-primary-600 hover:text-primary-700"
              @click="resetFilters"
            >
              Clear all
            </button>
          </div>

          <div class="space-y-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Source</label
              >
              <Listbox v-model="selectedSources" multiple>
                <div class="relative">
                  <ListboxButton
                    class="relative w-full cursor-pointer rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <span
                      v-if="selectedSources.length === 0"
                      class="text-gray-400"
                    >
                      All sources
                    </span>
                    <span v-else class="block truncate">
                      {{ selectedSources.length }} source{{
                        selectedSources.length > 1 ? 's' : ''
                      }}
                      selected
                    </span>
                    <span
                      class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
                    >
                      <ChevronUpDownIcon class="h-5 w-5 text-gray-400" />
                    </span>
                  </ListboxButton>

                  <transition
                    leave-active-class="transition ease-in duration-100"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                  >
                    <ListboxOptions
                      class="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
                    >
                      <ListboxOption
                        v-for="source in availableSources"
                        :key="source.id"
                        v-slot="{ active, selected }"
                        :value="source.id"
                        as="template"
                      >
                        <li
                          :class="[
                            active
                              ? 'bg-primary-100 text-primary-900'
                              : 'text-gray-900',
                            'relative cursor-pointer select-none py-2 pl-10 pr-4',
                          ]"
                        >
                          <span
                            :class="[
                              selected ? 'font-medium' : 'font-normal',
                              'block truncate',
                            ]"
                          >
                            {{ source.name }}
                          </span>
                          <span
                            v-if="selected"
                            class="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-600"
                          >
                            <CheckIcon class="h-5 w-5" />
                          </span>
                        </li>
                      </ListboxOption>
                    </ListboxOptions>
                  </transition>
                </div>
              </Listbox>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >PDF</label
              >
              <div class="flex gap-2">
                <button
                  v-for="option in pdfOptions"
                  :key="option.value"
                  :class="[
                    'px-3 py-1.5 text-sm rounded-md border transition-colors',
                    localHasPdfFilter === option.value
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50',
                  ]"
                  @click="localHasPdfFilter = option.value"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                v-model="localTitleFilter"
                type="text"
                placeholder="e.g. clinical trial|study"
                class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Author
              </label>
              <input
                v-model="localAuthorFilter"
                type="text"
                placeholder="e.g. Smith|Johnson"
                class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-gray-700"
                >Favourites only</label
              >
              <button
                :class="[
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                  showOnlyFavourites ? 'bg-primary-600' : 'bg-gray-200',
                ]"
                role="switch"
                :aria-checked="showOnlyFavourites"
                @click="toggleFavoriteFilter"
              >
                <span
                  :class="[
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    showOnlyFavourites ? 'translate-x-5' : 'translate-x-0',
                  ]"
                />
              </button>
            </div>

            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-gray-700"
                >Has screening AI suggestion</label
              >
              <button
                :class="[
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                  hasScreeningSuggestion ? 'bg-primary-600' : 'bg-gray-200',
                ]"
                role="switch"
                :aria-checked="hasScreeningSuggestion"
                @click="toggleScreeningSuggestionFilter"
              >
                <span
                  :class="[
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    hasScreeningSuggestion ? 'translate-x-5' : 'translate-x-0',
                  ]"
                />
              </button>
            </div>

            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-gray-700"
                >Has extraction AI suggestion</label
              >
              <button
                :class="[
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                  hasExtractionSuggestion ? 'bg-primary-600' : 'bg-gray-200',
                ]"
                role="switch"
                :aria-checked="hasExtractionSuggestion"
                @click="toggleExtractionSuggestionFilter"
              >
                <span
                  :class="[
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    hasExtractionSuggestion ? 'translate-x-5' : 'translate-x-0',
                  ]"
                />
              </button>
            </div>
          </div>
        </div>
      </template>
    </FloatingMenu>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '../../use-review'
import { createDebounce } from '@app/utils/debounce'
import { PdfFilterType } from './use-studies-filtering'
import FloatingMenu from '@app/components/Global/FloatingMenu.vue'
import { AdjustmentsVerticalIcon } from '@heroicons/vue/24/outline'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/vue'
import { StudyState } from '@app/types'

const review = injectStrict(ReviewKey)
const route = useRoute()
const router = useRouter()

const pdfOptions: { value: PdfFilterType; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'has', label: 'Has PDF' },
  { value: 'none', label: 'No PDF' },
]

const availableSources = computed(() => {
  const sources = new Map<string, { id: string; name: string }>()
  review.entity.value.searches?.forEach((search) => {
    if (search.source && !sources.has(search.source.id)) {
      sources.set(search.source.id, {
        id: search.source.id,
        name: search.source.name,
      })
    }
  })
  return Array.from(sources.values())
})

const selectedSources = computed({
  get: () => {
    const queryParam = route.query.sources
    if (!queryParam) return review.filtering.sourceFilter.value
    return Array.isArray(queryParam) ? queryParam : [queryParam]
  },
  set: (value: string[]) => {
    review.filtering.sourceFilter.value = value
    router.replace({
      query: {
        ...route.query,
        sources: value.length > 0 ? value : undefined,
        page: '1',
      },
    })
  },
})

const localHasPdfFilter = computed({
  get: () => {
    const queryParam = route.query.hasPdf as PdfFilterType | undefined
    if (queryParam && ['all', 'has', 'none'].includes(queryParam)) {
      return queryParam
    }
    return review.filtering.hasPdfFilter.value
  },
  set: (value: PdfFilterType) => {
    review.filtering.hasPdfFilter.value = value
    router.replace({
      query: {
        ...route.query,
        hasPdf: value !== 'all' ? value : undefined,
        page: '1',
      },
    })
  },
})

const localTitleFilter = ref(
  (route.query.title as string) || review.filtering.titleFilter.value,
)
const localAuthorFilter = ref(
  (route.query.author as string) || review.filtering.authorFilter.value,
)

const debounce = createDebounce(1000)

watch(localTitleFilter, (value) => {
  debounce(() => {
    review.filtering.titleFilter.value = value
    router.replace({
      query: {
        ...route.query,
        title: value || undefined,
        page: '1',
      },
    })
  })
})

watch(localAuthorFilter, (value) => {
  debounce(() => {
    review.filtering.authorFilter.value = value
    router.replace({
      query: {
        ...route.query,
        author: value || undefined,
        page: '1',
      },
    })
  })
})

watch(
  () => route.query,
  (query) => {
    if (query.sources) {
      const sources = Array.isArray(query.sources)
        ? query.sources
        : [query.sources]
      review.filtering.sourceFilter.value = sources as string[]
    }
    if (
      query.hasPdf &&
      ['all', 'has', 'none'].includes(query.hasPdf as string)
    ) {
      review.filtering.hasPdfFilter.value = query.hasPdf as PdfFilterType
    }
    if (query.title !== undefined) {
      review.filtering.titleFilter.value = query.title as string
      localTitleFilter.value = query.title as string
    }
    if (query.author !== undefined) {
      review.filtering.authorFilter.value = query.author as string
      localAuthorFilter.value = query.author as string
    }
    if (query.hasScreeningSuggestion !== undefined) {
      review.filtering.hasScreeningSuggestionFilter.value =
        query.hasScreeningSuggestion === 'true'
    }
    if (query.hasExtractionSuggestion !== undefined) {
      review.filtering.hasExtractionSuggestionFilter.value =
        query.hasExtractionSuggestion === 'true'
    }
  },
  { immediate: true },
)

const hasActiveFilters = computed(() => {
  return (
    selectedSources.value.length > 0 ||
    localHasPdfFilter.value !== 'all' ||
    localTitleFilter.value.length > 0 ||
    localAuthorFilter.value.length > 0 ||
    showOnlyFavourites.value ||
    hasScreeningSuggestion.value ||
    hasExtractionSuggestion.value
  )
})

const activeFilterCount = computed(() => {
  let count = 0
  if (selectedSources.value.length > 0) count++
  if (localHasPdfFilter.value !== 'all') count++
  if (localTitleFilter.value.length > 0) count++
  if (localAuthorFilter.value.length > 0) count++
  if (showOnlyFavourites.value) count++
  if (hasScreeningSuggestion.value) count++
  if (hasExtractionSuggestion.value) count++
  return count
})

const showOnlyFavourites = computed(() => {
  const filters = review.filtering.filters.value ?? {}
  return Object.keys(filters).every(
    (k) =>
      (k === StudyState.FAVOURITE && filters[k] === true) ||
      (k !== StudyState.FAVOURITE && filters[k as StudyState] === false),
  )
})

const hasScreeningSuggestion = computed({
  get: () => {
    const queryParam = route.query.hasScreeningSuggestion
    if (queryParam === 'true') return true
    return review.filtering.hasScreeningSuggestionFilter.value
  },
  set: (value: boolean) => {
    review.filtering.hasScreeningSuggestionFilter.value = value
    router.replace({
      query: {
        ...route.query,
        hasScreeningSuggestion: value ? 'true' : undefined,
        page: '1',
      },
    })
  },
})

const hasExtractionSuggestion = computed({
  get: () => {
    const queryParam = route.query.hasExtractionSuggestion
    if (queryParam === 'true') return true
    return review.filtering.hasExtractionSuggestionFilter.value
  },
  set: (value: boolean) => {
    review.filtering.hasExtractionSuggestionFilter.value = value
    router.replace({
      query: {
        ...route.query,
        hasExtractionSuggestion: value ? 'true' : undefined,
        page: '1',
      },
    })
  },
})

function toggleFavoriteFilter() {
  if (showOnlyFavourites.value) {
    review.filtering.resetFilters()
  } else {
    review.filtering.filterStudiesByState(StudyState.FAVOURITE)
  }
  router.replace({
    query: {
      ...route.query,
      page: '1',
    },
  })
}

function toggleScreeningSuggestionFilter() {
  hasScreeningSuggestion.value = !hasScreeningSuggestion.value
}

function toggleExtractionSuggestionFilter() {
  hasExtractionSuggestion.value = !hasExtractionSuggestion.value
}

function resetFilters() {
  selectedSources.value = []
  localHasPdfFilter.value = 'all'
  localTitleFilter.value = ''
  localAuthorFilter.value = ''
  hasScreeningSuggestion.value = false
  hasExtractionSuggestion.value = false
  review.filtering.sourceFilter.value = []
  review.filtering.hasPdfFilter.value = 'all'
  review.filtering.titleFilter.value = ''
  review.filtering.authorFilter.value = ''
  review.filtering.hasScreeningSuggestionFilter.value = false
  review.filtering.hasExtractionSuggestionFilter.value = false
  review.filtering.resetFilters()
  router.replace({
    query: {
      ...route.query,
      sources: undefined,
      hasPdf: undefined,
      title: undefined,
      author: undefined,
      hasScreeningSuggestion: undefined,
      hasExtractionSuggestion: undefined,
      page: '1',
    },
  })
}
</script>
